// Updated useSearchInterface.js - Cleaned version
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calculatePrebuiltDomainSets } from "../components/prebuiltDomainSetsCalculator";
import apiService from "../services/apiService";
import { getRecommendedThreshold, relevanceFromDistance } from "../utils";

// Fallback cosine distance for terms rebuilt from an API payload
// (threshold sheet: Domains -> Strong Match).
const DEFAULT_TERM_THRESHOLD = getRecommendedThreshold("DOMAINS");

export const useSearchInterface = (
	initialFilters = {},
	activeTab = "domains",
	currentFilters = null,
) => {
	// Core search state
	const [searchQuery, setSearchQuery] = useState("");
	const [refinedQuery, setRefinedQuery] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [searchSuggestions, setSearchSuggestions] = useState([]);

	const [searchResults, setSearchResults] = useState({
		domains: [],
		keywords: [],
		totalRecords: 0,
		searchSummary: null,
	});

	const [prebuiltDomainSets, setPrebuiltDomainSets] = useState({});
	const [error, setError] = useState(null);

	// Performance metrics state
	const [performanceMetric, setPerformanceMetric] = useState("viewability");
	const [minReachThreshold, setMinReachThreshold] = useState("1");

	// Debounce timer for suggestions
	const suggestionTimer = useRef(null);

	// Memoize stable values to prevent unnecessary re-renders
	const stableInitialFilters = useMemo(
		() => initialFilters,
		[JSON.stringify(initialFilters)],
	);

	// Handle search input changes and fetch suggestions
	const handleSearchInputChange = useCallback(async (e) => {
		const value = e.target.value;
		setSearchQuery(value);

		// Clear previous timer
		if (suggestionTimer.current) {
			clearTimeout(suggestionTimer.current);
		}

		if (value.length >= 2) {
			setShowSuggestions(true);

			// Debounce suggestions fetch
			suggestionTimer.current = setTimeout(async () => {
				try {
					const result = await apiService.getKeywords(value);

					if (result.success && Array.isArray(result.keywords)) {
						setSearchSuggestions(result.keywords.slice(0, 8));
					} else {
						setSearchSuggestions([]);
					}
				} catch (err) {
					setSearchSuggestions([]);
				}
			}, 300);
		} else {
			setShowSuggestions(false);
			setSearchSuggestions([]);
		}
	}, []);

	// Main search function
	const handleSearch = useCallback(
		async (searchData, searchOptions = {}) => {
			try {
				setIsSearching(true);
				setError(null);
				const { overrideResults } = searchOptions;

				if (overrideResults) {
					// Use provided LLM results directly
					const llmResults = overrideResults;

					// Calculate prebuilt domain sets from LLM results
					let calculatedPrebuiltSets = {};
					try {
						const data =
							llmResults.originalData?.body ||
							llmResults.originalData ||
							llmResults;
						calculatedPrebuiltSets = calculatePrebuiltDomainSets(
							data,
							"viewability",
						);
						setPrebuiltDomainSets(calculatedPrebuiltSets);
					} catch (prebuiltError) {
						setPrebuiltDomainSets({});
					}

					// Set the LLM results as search results
					setSearchResults({
						...llmResults,
						prebuiltSets: calculatedPrebuiltSets,
					});

					return {
						success: true,
						data:
							llmResults.originalData?.body ||
							llmResults.originalData ||
							llmResults,
						prebuiltSets: calculatedPrebuiltSets,
						metadata: {
							isLLMEnhanced: true,
							originalQuery: llmResults.originalData?.metadata?.originalQuery,
						},
					};
				}

				// Transform SearchInterface format to expected format
				let searchTerms = [];

				// Check if searchData is in SearchInterface format
				if (
					searchData &&
					typeof searchData === "object" &&
					!Array.isArray(searchData)
				) {
					// Add include terms from query array
					if (searchData.query && Array.isArray(searchData.query)) {
						searchData.query.forEach((item) => {
							searchTerms.push({
								text: item.query,
								type: "include",
								threshold: item.threshold ?? DEFAULT_TERM_THRESHOLD,
								score: Math.round(
									relevanceFromDistance(
										item.threshold ?? DEFAULT_TERM_THRESHOLD,
										"DOMAINS",
									),
								),
							});
						});
					}

					// Add exclude terms from excludeQuery array
					if (
						searchData.excludeQuery &&
						Array.isArray(searchData.excludeQuery)
					) {
						searchData.excludeQuery.forEach((item) => {
							searchTerms.push({
								text: item.query,
								type: "exclude",
								threshold: item.threshold ?? DEFAULT_TERM_THRESHOLD,
								score: Math.round(
									relevanceFromDistance(
										item.threshold ?? DEFAULT_TERM_THRESHOLD,
										"DOMAINS",
									),
								),
							});
						});
					}
				}
				// If searchData is already in the expected array format
				else if (Array.isArray(searchData)) {
					searchTerms = searchData;
				}
				// If it's a simple string
				else if (typeof searchData === "string") {
					searchTerms = [
					{
						text: searchData,
						type: "include",
						threshold: DEFAULT_TERM_THRESHOLD,
						score: Math.round(
							relevanceFromDistance(DEFAULT_TERM_THRESHOLD, "DOMAINS"),
						),
					},
				];
				}

				// Check authentication
				if (!apiService.isAuthenticated()) {
					if (apiService.handleAuthFailure) {
						apiService.handleAuthFailure(new Error("Authentication required"));
					}
					return { success: false, error: "Authentication required" };
				}

				// Extract options with proper filters handling
				const {
					performanceMetric: optionsPerformanceMetric = performanceMetric,
					minValue = parseInt(minReachThreshold) || 0,
					performanceGoalType = "reach",
					dateRange = 30,
					filters: passedFilters,
					sortBy,
					sortOrder,
					overrideFilterSort = false,
				} = searchOptions;

				// Use currentFilters if provided, otherwise use stableInitialFilters
				let filtersToUse;

				if (
					passedFilters &&
					(passedFilters.thresholdType || passedFilters.type)
				) {
					filtersToUse = passedFilters;
				} else {
					filtersToUse = currentFilters || stableInitialFilters;
				}

				if (sortBy || sortOrder) {
					filtersToUse = {
						...filtersToUse,
						sortBy: sortBy || filtersToUse.sortBy || "compositeScore",
						sortOrder: sortOrder || filtersToUse.sortOrder || "desc",
						view: sortOrder || filtersToUse.sortOrder || "desc",
					};
				}

				// Map performance goal type to API format
				let apiPerformanceMetric = optionsPerformanceMetric;
				if (performanceGoalType === "market_request") {
					apiPerformanceMetric = "reach";
				}

				// Call API
				const result = await apiService.performDomainSearch(
					searchTerms,
					filtersToUse,
					activeTab,
					apiPerformanceMetric,
					minValue,
				);

				if (!result.success) {
					if (
						result.error?.includes("Authentication failed") ||
						result.error?.includes("Token refresh failed") ||
						result.error?.includes("unable to obtain valid token")
					) {
						return { success: false, error: "Authentication failed" };
					}
					throw new Error(result.error || "Search failed - no data returned");
				}

				if (result.success) {
					const responseData = result.data || {};
					const body = responseData.body || responseData;

					// Calculate prebuilt domain sets from API response
					let calculatedPrebuiltSets = {};

					try {
						calculatedPrebuiltSets = calculatePrebuiltDomainSets(
							body,
							apiPerformanceMetric,
						);
						setPrebuiltDomainSets(calculatedPrebuiltSets);
					} catch (prebuiltError) {
						setPrebuiltDomainSets({});
					}

					// Store results with the original format
					setSearchResults({
						domains: body.domain || [],
						keywords: body.keywords || [],
						totalRecords: body.totalRecords || 0,
						searchSummary: null,
						prebuiltSets: calculatedPrebuiltSets,

						originalData: {
							body: body,
							statusCode: responseData.statusCode || result.statusCode,
							metadata: {
								searchTime: new Date().toISOString(),
								searchTerms: searchTerms,
								filters: filtersToUse,
								performanceMetric: apiPerformanceMetric,
								minValue: minValue,
								isLLMEnhanced: false,
							},
						},

						success: true,
						appliedFilters: filtersToUse,
					});

					return {
						success: true,
						data: body,
						prebuiltSets: calculatedPrebuiltSets,
						metadata: {
							searchTerms,
							filters: filtersToUse,
							performanceMetric: apiPerformanceMetric,
							isLLMEnhanced: false,
						},
					};
				}
			} catch (err) {
				// Handle authentication errors gracefully
				if (
					err.message?.includes("Authentication failed") ||
					err.message?.includes("Token refresh failed") ||
					err.message?.includes("unable to obtain valid token")
				) {
					setPrebuiltDomainSets({});
					return { success: false, error: "Authentication failed" };
				}

				// For other errors, set error state
				const errorMessage = err.message || "Search failed";
				setError(errorMessage);
				setPrebuiltDomainSets({});

				return { success: false, error: errorMessage };
			} finally {
				setIsSearching(false);
			}
		},
		[
			performanceMetric,
			minReachThreshold,
			activeTab,
			stableInitialFilters,
			currentFilters,
		],
	);

	const handleSearchWithLogging = useCallback(
		async (searchData, searchOptions = {}) => {
			return handleSearch(searchData, searchOptions);
		},
		[handleSearch],
	);

	// Handle suggestion selection
	const handleSuggestionSelect = useCallback((suggestion) => {
		try {
			let suggestionText = "";

			if (typeof suggestion === "string") {
				suggestionText = suggestion;
			} else if (suggestion?.keyword) {
				suggestionText = suggestion.keyword;
			} else if (suggestion?.text) {
				suggestionText = suggestion.text;
			} else if (suggestion?.name) {
				suggestionText = suggestion.name;
			}

			if (suggestionText.trim()) {
				setSearchQuery(suggestionText);
				setShowSuggestions(false);
			}
		} catch (err) {
			// Silent error handling
		}
	}, []);

	// Handle refined query changes
	const handleRefinedQueryChange = useCallback((value) => {
		setRefinedQuery(value);
	}, []);

	// Clear refinement and reset to initial search
	const handleClearRefinement = useCallback(() => {
		setRefinedQuery("");
		setSearchResults(null);
		setPrebuiltDomainSets({});
		setError(null);
	}, []);

	// Hide suggestions dropdown
	const handleHideSuggestions = useCallback(() => {
		setShowSuggestions(false);
	}, []);

	// Handle performance metric changes
	const handlePerformanceMetricChange = useCallback((metric) => {
		setPerformanceMetric(metric);
	}, []);

	// Handle min reach threshold changes
	const handleMinReachChange = useCallback((value) => {
		setMinReachThreshold(value);
	}, []);

	// Reset all search state
	const resetSearch = useCallback(() => {
		setSearchQuery("");
		setRefinedQuery("");
		setIsSearching(false);
		setShowSuggestions(false);
		setSearchSuggestions([]);
		setSearchResults(null);
		setPrebuiltDomainSets({});
		setError(null);

		// Clear any pending suggestion timer
		if (suggestionTimer.current) {
			clearTimeout(suggestionTimer.current);
		}
	}, []);

	// Get search summary with prebuilt sets info
	const getSearchSummary = useCallback(() => {
		if (!searchResults) return null;

		return {
			totalRecords: searchResults.totalRecords || 0,
			domains: searchResults.domains?.length || 0,
			keywords: searchResults.keywords?.length || 0,
			cumulativeData: searchResults.originalData?.cumulativeData?.length || 0,
			prebuiltSets: Object.keys(prebuiltDomainSets).length,
			searchTime: searchResults.originalData?.metadata?.searchTime,
			appliedFilters: searchResults.appliedFilters,
		};
	}, [searchResults, prebuiltDomainSets]);

	// Function to manually recalculate prebuilt sets
	const recalculatePrebuiltSets = useCallback(() => {
		if (searchResults?.originalData?.body || searchResults?.originalData) {
			try {
				const data =
					searchResults.originalData.body || searchResults.originalData;
				const calculatedSets = calculatePrebuiltDomainSets(data);
				setPrebuiltDomainSets(calculatedSets);
				return calculatedSets;
			} catch (error) {
				return {};
			}
		}
		return {};
	}, [searchResults]);

	// Memoized computed values for better performance
	const computedValues = useMemo(
		() => ({
			hasResults:
				searchResults?.domains?.length > 0 ||
				searchResults?.keywords?.length > 0,
			hasPrebuiltSets: Object.keys(prebuiltDomainSets).length > 0,
			hasError: !!error,
		}),
		[searchResults, prebuiltDomainSets, error],
	);

	// Cleanup effect for timer
	useEffect(() => {
		return () => {
			if (suggestionTimer.current) {
				clearTimeout(suggestionTimer.current);
			}
		};
	}, []);

	// Cleanup function
	const cleanup = useCallback(() => {
		if (suggestionTimer.current) {
			clearTimeout(suggestionTimer.current);
		}
	}, []);

	return {
		// Core search state
		searchQuery,
		refinedQuery,
		isSearching,
		showSuggestions,
		searchSuggestions,
		searchResults,
		error,

		// Prebuilt domain sets
		prebuiltDomainSets,

		// Performance state
		performanceMetric,
		minReachThreshold,

		// Event handlers for SearchInterface component
		onSearchInputChange: handleSearchInputChange,
		onRefinedQueryChange: handleRefinedQueryChange,
		onSearch: handleSearchWithLogging,
		onSuggestionSelect: handleSuggestionSelect,
		onClearRefinement: handleClearRefinement,
		onHideSuggestions: handleHideSuggestions,
		onPerformanceMetricChange: handlePerformanceMetricChange,
		onMinReachChange: handleMinReachChange,

		// Additional utility functions
		resetSearch,
		getSearchSummary,
		recalculatePrebuiltSets,
		cleanup,

		// Computed values (memoized)
		...computedValues,
	};
};
